import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthenticated, redeemCode as redeemCodeApi } from '@alchemiesolns/product-registration';
import { INTENT_QUERY_KEY, appendIntentToPath, buildAuthRedirectSearch } from '../auth/navigation';
import { signOutApp } from '../auth/signOutApp';
import { useAuthUser } from '@alchemiesolns/user-access';
import {
  fetchProductPricingPreview,
  formatLicenseHint,
  startCheckoutSession,
  startBillingPortalSession,
  useLicense,
  type ProductPricingPreview,
} from '@alchemiesolns/store-api';
import { tryEstablishLicense } from '../loginWithFirebase';
import { PRODUCT_ID } from '../constants/product';
import piphWordmarkUrl from '../assets/piph-wordmark.png';
import { BackArrowIcon } from '@alchemiesolns/ui';
import './UpgradePage.css';

/**
 * Paid checkout uses the Firestore `stripePriceIds` key (`yearly`) until commerce adds aliases.
 * URL `intent=` uses user-facing `subscribe`.
 */
const CHECKOUT_INTENT_PAID_PRICE_KEY = 'yearly';
const UPGRADE_URL_INTENT_SUBSCRIBE = 'subscribe';

const PLAN_CARD = {
  name: 'Piph Author — Full access',
  subscriptionSummary:
    'A subscription unlocks uninterrupted use of Piph Author, including exports, CDXML import, and updates for the licensed term. After checkout you can manage payment methods, invoices, and renewal from the billing portal.',
  includesHeading: "What's included:",
  features: [
    'SVG & PNG export with alt text',
    'ChemDraw CDXML import',
    'Re-import previously created Piph images',
    'Access to improvements while your license is active',
  ],
  cta: 'Subscribe',
} as const;

/**
 * Paywall: requires Firebase session; license session optional until checkout completes.
 */
export function UpgradePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, ready } = useAuthUser();
  const bypassBilling = import.meta.env.VITE_BYPASS_BILLING === 'true';
  const licenseState = useLicense(PRODUCT_ID, { enabled: ready && !!user });
  const [checkoutError, setCheckoutError] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [pricingPreview, setPricingPreview] = useState<ProductPricingPreview | null>(null);
  const [pricingResolved, setPricingResolved] = useState(false);

  const billingConfigured = Boolean(import.meta.env.VITE_API_BASE?.trim());
  const showVerbosePricingReason =
    import.meta.env.DEV || import.meta.env.VITE_UPGRADE_SHOW_FALLBACK_REASON === 'true';
  const licenseHint = ready && user ? formatLicenseHint(licenseState, PRODUCT_ID) : null;
  const isOverlay = Boolean((location.state as { backgroundLocation?: unknown } | null)?.backgroundLocation);
  const isPricingLoading = billingConfigured && !!user && !pricingResolved;

  useEffect(() => {
    if (!billingConfigured || !user) {
      setPricingResolved(false);
      return;
    }
    let cancelled = false;
    setPricingResolved(false);
    void (async () => {
      const p = await fetchProductPricingPreview(PRODUCT_ID);
      if (!cancelled) {
        setPricingPreview(p);
        setPricingResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingConfigured, user]);

  const { priceLine, billingSubline, pricingSource } = useMemo(() => {
    const fromApiPrice = pricingPreview?.formattedPriceLine?.trim();
    const fromApiBilling = pricingPreview?.billingIntervalLabel?.trim();
    const hasApiPricing = Boolean(fromApiPrice || fromApiBilling);
    if (isPricingLoading) {
      return {
        priceLine: null,
        billingSubline: null,
        pricingSource: 'loading' as const,
      };
    }
    if (!hasApiPricing) {
      return {
        priceLine: null,
        billingSubline: null,
        pricingSource: 'loading' as const,
      };
    }

    return {
      priceLine: fromApiPrice,
      billingSubline: fromApiBilling ?? '',
      pricingSource: 'api' as const,
    };
  }, [isPricingLoading, pricingPreview]);

  const goCheckoutSubscribe = useCallback(async () => {
    setCheckoutError('');
    setRedeemError('');
    setBusy(true);
    try {
      const origin = window.location.origin;
      const base = import.meta.env.BASE_URL;
      await startCheckoutSession({
        productId: PRODUCT_ID,
        intent: CHECKOUT_INTENT_PAID_PRICE_KEY,
        successUrl: `${origin}${base}billing/return`,
        cancelUrl: `${origin}${base}upgrade?${INTENT_QUERY_KEY}=${UPGRADE_URL_INTENT_SUBSCRIBE}`,
      });
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Could not start checkout.');
    } finally {
      setBusy(false);
    }
  }, []);

  const onRedeemCode = useCallback(async () => {
    setCheckoutError('');
    setRedeemError('');
    setBusy(true);
    try {
      await redeemCodeApi(redeemCode);
      navigate(appendIntentToPath('/app', searchParams), { replace: true });
    } catch (e) {
      setRedeemError(e instanceof Error ? e.message : 'Could not apply this code.');
    } finally {
      setBusy(false);
    }
  }, [navigate, redeemCode, searchParams]);

  const refreshLicense = useCallback(async () => {
    setCheckoutError('');
    setRedeemError('');
    setBusy(true);
    try {
      const ok = await tryEstablishLicense(PRODUCT_ID);
      if (ok) {
        navigate(appendIntentToPath('/app', searchParams), { replace: true });
      } else {
        setCheckoutError('No license found yet. If you just paid, wait a moment and try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [navigate, searchParams]);

  const upgradeReturnUrl = useCallback(() => {
    const root = `${window.location.origin}${import.meta.env.BASE_URL}upgrade`;
    const intent = searchParams.get(INTENT_QUERY_KEY);
    if (!intent) return root;
    return `${root}?${INTENT_QUERY_KEY}=${encodeURIComponent(intent)}`;
  }, [searchParams]);

  const onManageBilling = useCallback(async () => {
    setCheckoutError('');
    setRedeemError('');
    setBusy(true);
    try {
      await startBillingPortalSession({
        productId: PRODUCT_ID,
        returnUrl: upgradeReturnUrl(),
      });
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Could not open billing portal.');
    } finally {
      setBusy(false);
    }
  }, [upgradeReturnUrl]);

  const onSignOut = useCallback(async () => {
    setCheckoutError('');
    setRedeemError('');
    setBusy(true);
    try {
      await signOutApp();
      navigate(`/login${buildAuthRedirectSearch(searchParams)}`, { replace: true });
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Sign out failed.');
    } finally {
      setBusy(false);
    }
  }, [navigate, searchParams]);

  const onBackFromOverlay = useCallback(() => {
    navigate(appendIntentToPath('/app', searchParams), { replace: true });
  }, [navigate, searchParams]);

  if (bypassBilling) {
    return <Navigate to={appendIntentToPath('/app', searchParams)} replace />;
  }

  if (!ready) {
    return <div className="upgrade-loading">Loading…</div>;
  }

  if (isAuthenticated()) {
    return <Navigate to={appendIntentToPath('/app', searchParams)} replace />;
  }

  if (!user) {
    const qs = new URLSearchParams();
    const intent = searchParams.get(INTENT_QUERY_KEY);
    if (intent) qs.set(INTENT_QUERY_KEY, intent);
    qs.set('return', '/upgrade');
    return <Navigate to={`/login?${qs.toString()}`} replace />;
  }

  return (
    <div className={`upgrade-page-shell${isOverlay ? ' upgrade-page-shell--overlay' : ''}`}>
      <div className="upgrade-page-frame">
        <div
          className="upgrade-page-card"
          role={isOverlay ? 'dialog' : 'region'}
          aria-modal={isOverlay ? true : undefined}
          aria-labelledby="upgrade-heading"
        >
          <header className="upgrade-page-header">
            {isOverlay && (
              <button
                type="button"
                className="upgrade-page-back"
                onClick={onBackFromOverlay}
                disabled={busy}
                aria-label="Back to app"
              >
                <BackArrowIcon width={16} height={16} />
              </button>
            )}
            <div className="upgrade-page-kicker upgrade-page-kicker--brand">
              <span className="upgrade-page-kicker-logo-wrap">
                <img src={piphWordmarkUrl} alt="Piph" className="upgrade-page-kicker-logo" />
              </span>
              <span className="upgrade-page-kicker-text">by Alchemie</span>
            </div>
          </header>

          <div className="upgrade-page-body">
            <p className="upgrade-page-eyebrow">Pricing and subscription</p>
            <h1 id="upgrade-heading" className="upgrade-page-title upgrade-page-title--pricing">
              Subscribe or redeem a code to continue
            </h1>

            {user.email && (
              <p className="upgrade-page-user">
                <span className="upgrade-page-user-label">Signed in as</span>
                <span className="upgrade-page-user-email">{user.email}</span>
              </p>
            )}

            {licenseHint && <p className="upgrade-page-note">{licenseHint}</p>}

            {checkoutError && (
              <p className="upgrade-page-note upgrade-page-note--error" role="alert">
                {checkoutError}
              </p>
            )}

            {redeemError && (
              <p className="upgrade-page-note upgrade-page-note--error" role="alert">
                {redeemError}
              </p>
            )}

            <div className="upgrade-page-plans">
              <article className="upgrade-plan-card upgrade-plan-card--subscribe">
                <h2 className="upgrade-plan-card__name">{PLAN_CARD.name}</h2>
                {pricingSource === 'loading' ? (
                  <div className="upgrade-plan-card__pricing-skeleton" aria-hidden="true">
                    <div className="upgrade-plan-card__skeleton upgrade-plan-card__skeleton--price" />
                    <div className="upgrade-plan-card__skeleton upgrade-plan-card__skeleton--subline" />
                  </div>
                ) : (
                  <>
                    <p className="upgrade-plan-card__price">{priceLine}</p>
                    <p className="upgrade-plan-card__subline">{billingSubline}</p>
                  </>
                )}
                <p className="upgrade-plan-card__subscription-detail">{PLAN_CARD.subscriptionSummary}</p>
                <p className="upgrade-plan-card__includes">
                  <strong>{PLAN_CARD.includesHeading}</strong>
                </p>
                <div className="upgrade-plan-card__features" role="list">
                  {PLAN_CARD.features.map((feature) => (
                    <div key={feature} className="upgrade-plan-card__feature" role="listitem">
                      <span className="upgrade-plan-card__check" aria-hidden="true">
                        ✓
                      </span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void goCheckoutSubscribe()}
                  className="upgrade-btn upgrade-btn--secondary upgrade-plan-card__action"
                >
                  {PLAN_CARD.cta}
                </button>
              </article>
            </div>

            <p className="upgrade-page-pricing-footnote">
              List price is loaded from our billing API&apos;s live catalog. If the API is temporarily unavailable, this
              app falls back to local configuration so you can still compare before checkout. Taxes, discounts, and
              the final charge are always confirmed in Stripe Checkout before you pay.
            </p>
            {showVerbosePricingReason && pricingSource !== 'api' && pricingSource !== 'loading' && pricingPreview?.unavailableReason && (
              <p className="upgrade-page-pricing-footnote" role="status">
                Debug: pricing fallback reason: {pricingPreview.unavailableReason}
              </p>
            )}

            <section className="upgrade-page-redeem" aria-labelledby="upgrade-redeem-heading">
              <h2 id="upgrade-redeem-heading" className="upgrade-page-redeem__title">
                Redeem Code
              </h2>
              <div className="upgrade-page-redeem__row">
                <input
                  id="upgrade-redeem-input"
                  type="text"
                  className="upgrade-page-redeem__input"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="REDEEMCODE"
                  aria-label="Redemption code"
                />
                <button
                  type="button"
                  disabled={busy || !redeemCode.trim()}
                  onClick={() => void onRedeemCode()}
                  className="upgrade-btn upgrade-btn--primary upgrade-page-redeem__submit"
                >
                  Apply code
                </button>
              </div>
            </section>

            <div className="upgrade-page-aux">
              <button type="button" disabled={busy} onClick={() => void refreshLicense()} className="upgrade-aux-link">
                Refresh license
              </button>
            </div>

            <footer className="upgrade-page-footer">
              {billingConfigured && (
                <button type="button" disabled={busy} onClick={() => void onManageBilling()} className="upgrade-aux-link">
                  Manage billing
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => void onSignOut()}
                className="upgrade-aux-link upgrade-aux-link--signout"
              >
                Sign out
              </button>
              <Link className="upgrade-aux-link upgrade-aux-link--nav" to="/">
                Home
              </Link>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
